/**
 * Store Module - Cart Page
 *
 * Full shopping cart: item list with quantity controls, remove, subtotals,
 * cart total, clear-cart, and continue-shopping navigation.
 * Uses Zustand cart store and tf-* design tokens.
 */
import { Link, useLocation } from 'react-router-dom'
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react'
import {
  useCartStore,
  selectCartTotal,
  selectCartItemCount,
} from '../store/cart-store'
import type { CartItem } from '../store/cart-store'
import { FunnelExperience, FunnelRuntimeProvider } from '../../funnels/runtime'

// ── Sub-components ──────────────────────────────────────────────────────────

interface CartItemRowProps {
  item: CartItem
  onIncrement: (productId: string, currentQty: number) => void
  onDecrement: (productId: string, currentQty: number) => void
  onRemove: (productId: string) => void
}

function CartItemRow({
  item,
  onIncrement,
  onDecrement,
  onRemove,
}: CartItemRowProps) {
  const subtotal = item.price * item.quantity

  return (
    <li className="flex flex-col gap-4 border-b border-tf-border py-5 sm:flex-row sm:items-center">
      {/* Product image */}
      <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-lg bg-tf-bg-muted">
        <img
          src={item.imageUrl}
          alt={item.name}
          className="h-full w-full object-cover"
        />
      </div>

      {/* Product info */}
      <div className="flex flex-1 flex-col gap-1">
        <p className="text-base font-semibold text-tf-text">{item.name}</p>
        {item.variantId && (
          <p className="text-xs text-tf-text-muted">Variant: {item.variantId}</p>
        )}
        <p className="text-sm font-medium text-tf-text-sub">
          ${item.price.toFixed(2)} each
        </p>
      </div>

      {/* Quantity controls */}
      <div className="flex items-center gap-2" role="group" aria-label={`Quantity for ${item.name}`}>
        <button
          type="button"
          aria-label={`Decrease quantity of ${item.name}`}
          onClick={() => onDecrement(item.productId, item.quantity)}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-tf-border bg-tf-bg-card text-tf-text transition-colors hover:bg-tf-bg-hover focus:outline-none focus:ring-2 focus:ring-tf-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Minus className="h-3 w-3" aria-hidden="true" />
        </button>
        <span
          className="w-8 text-center text-sm font-semibold text-tf-text"
          aria-live="polite"
          aria-atomic="true"
        >
          {item.quantity}
        </span>
        <button
          type="button"
          aria-label={`Increase quantity of ${item.name}`}
          onClick={() => onIncrement(item.productId, item.quantity)}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-tf-border bg-tf-bg-card text-tf-text transition-colors hover:bg-tf-bg-hover focus:outline-none focus:ring-2 focus:ring-tf-ring focus:ring-offset-2"
        >
          <Plus className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>

      {/* Subtotal */}
      <p className="w-20 text-right text-base font-bold text-tf-text">
        ${subtotal.toFixed(2)}
      </p>

      {/* Remove button */}
      <button
        type="button"
        aria-label={`Remove ${item.name} from cart`}
        onClick={() => onRemove(item.productId)}
        className="flex h-8 w-8 items-center justify-center rounded-md text-tf-text-muted transition-colors hover:bg-tf-critical-lighter hover:text-tf-critical focus:outline-none focus:ring-2 focus:ring-tf-ring focus:ring-offset-2"
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </button>
    </li>
  )
}

// ── Empty state ──────────────────────────────────────────────────────────────

function EmptyCart() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <ShoppingBag
        className="mb-4 h-16 w-16 text-tf-text-muted"
        aria-hidden="true"
      />
      <h2 className="mb-2 text-xl font-semibold text-tf-text">
        Your cart is empty
      </h2>
      <p className="mb-6 text-sm text-tf-text-sub">
        Looks like you haven't added anything yet.
      </p>
      <Link
        to="/store/products"
        className="inline-flex items-center gap-2 rounded-lg bg-tf-primary px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-tf-primary-hover focus:outline-none focus:ring-2 focus:ring-tf-ring focus:ring-offset-2"
      >
        Browse Products
      </Link>
    </div>
  )
}

// ── Cart Page ────────────────────────────────────────────────────────────────

function CartContent() {
  const location = useLocation()
  const items = useCartStore((s) => s.items)
  const updateQuantity = useCartStore((s) => s.updateQuantity)
  const removeItem = useCartStore((s) => s.removeItem)
  const clearCart = useCartStore((s) => s.clearCart)
  const total = useCartStore(selectCartTotal)
  const itemCount = useCartStore(selectCartItemCount)

  const handleIncrement = (productId: string, currentQty: number) => {
    updateQuantity(productId, currentQty + 1)
  }

  const handleDecrement = (productId: string, currentQty: number) => {
    updateQuantity(productId, currentQty - 1)
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Page header */}
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold text-tf-text">
          Shopping Cart
          {itemCount > 0 && (
            <span className="ml-3 text-xl font-normal text-tf-text-muted">
              ({itemCount} {itemCount === 1 ? 'item' : 'items'})
            </span>
          )}
        </h1>
        <Link
          to="/store/products"
          className="text-sm font-medium text-tf-text-link hover:underline focus:outline-none focus:ring-2 focus:ring-tf-ring focus:ring-offset-1 rounded"
        >
          ← Continue Shopping
        </Link>
      </div>
      <FunnelExperience
        pageType="cart"
        context={{ url: location.pathname }}
      />

      {items.length === 0 ? (
        <EmptyCart />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Cart item list */}
          <section
            aria-label="Cart items"
            className="lg:col-span-2"
          >
            <div className="rounded-lg bg-tf-bg-card shadow">
              <ul className="divide-y divide-tf-border px-6">
                {items.map((item) => (
                  <CartItemRow
                    key={item.productId}
                    item={item}
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    onRemove={removeItem}
                  />
                ))}
              </ul>
              <div className="flex justify-end px-6 py-4">
                <button
                  type="button"
                  onClick={clearCart}
                  className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-tf-critical transition-colors hover:bg-tf-critical-lighter focus:outline-none focus:ring-2 focus:ring-tf-ring focus:ring-offset-1"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Clear Cart
                </button>
              </div>
            </div>
          </section>

          {/* Order summary */}
          <aside aria-label="Order summary">
            <div className="rounded-lg bg-tf-bg-card shadow">
              <div className="border-b border-tf-border px-6 py-4">
                <h2 className="text-base font-semibold text-tf-text">
                  Order Summary
                </h2>
              </div>
              <div className="px-6 py-4">
                {/* Line items summary */}
                <ul className="space-y-2 text-sm text-tf-text-sub" aria-label="Line items">
                  {items.map((item) => (
                    <li key={item.productId} className="flex justify-between">
                      <span className="truncate pr-4 text-tf-text-sub">
                        {item.name} × {item.quantity}
                      </span>
                      <span className="font-medium text-tf-text">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Divider */}
                <div className="my-4 border-t border-tf-border" />

                {/* Total */}
                <div className="flex items-center justify-between">
                  <span className="text-base font-semibold text-tf-text">Total</span>
                  <span
                    className="text-xl font-extrabold text-tf-text"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    ${total.toFixed(2)}
                  </span>
                </div>

                <p className="mt-1 text-xs text-tf-text-muted">
                  Shipping and taxes calculated at checkout
                </p>

                {/* Checkout CTA */}
                <Link
                  to="/store/checkout"
                  className="mt-5 flex w-full items-center justify-center rounded-lg bg-tf-primary px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-tf-primary-hover focus:outline-none focus:ring-2 focus:ring-tf-ring focus:ring-offset-2"
                >
                  Proceed to Checkout
                </Link>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  )
}

export default function Cart() {
  return (
    <FunnelRuntimeProvider>
      <CartContent />
    </FunnelRuntimeProvider>
  )
}











